import { cn } from '@quadralize/utils'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { motion } from 'framer-motion'
import * as React from 'react'
import { ScrollArea } from '../scroll-area/scroll-area'

const TabsContext = React.createContext<{
  activeTab: string
  setActiveTab: (value: string) => void
  uniqueId: string
  stretch: boolean
}>({
  activeTab: '',
  setActiveTab: () => {},
  uniqueId: '',
  stretch: false,
})

const Tabs = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ children, onValueChange, defaultValue, value, ...props }, ref) => {
  const [activeTab, setActiveTab] = React.useState(value || defaultValue || '')
  const uniqueId = React.useId()

  const handleValueChange = (newValue: string) => {
    setActiveTab(newValue)
    onValueChange?.(newValue)
  }

  React.useEffect(() => {
    if (value) setActiveTab(value)
  }, [value])

  return (
    <TabsContext.Provider
      value={{ activeTab, setActiveTab: handleValueChange, uniqueId, stretch: false }}
    >
      <TabsPrimitive.Root
        ref={ref}
        onValueChange={handleValueChange}
        defaultValue={defaultValue}
        value={value}
        {...props}
      >
        {children}
      </TabsPrimitive.Root>
    </TabsContext.Provider>
  )
})
Tabs.displayName = TabsPrimitive.Root.displayName

type TabsListVariant = 'default' | 'line'

interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  variant?: TabsListVariant
  stretch?: boolean
}

const TabsList = React.forwardRef<React.ComponentRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, variant = 'default', stretch = false, ...props }, ref) => {
    const context = React.useContext(TabsContext)

    return (
      <TabsContext.Provider value={{ ...context, stretch }}>
        <ScrollArea
          type="hover"
          scrollbarSize="sm"
          className={cn(variant === 'line' || stretch ? 'w-full' : '')}
        >
          <TabsPrimitive.List
            ref={ref}
            className={cn(
              'inline-flex items-center justify-center text-text-tertiary relative',
              variant === 'default' && 'h-9 rounded-lg bg-bg-muted p-1 shadow-inner',
              variant === 'line' && 'h-10 border-b border-border-subtle gap-6 w-full justify-start',
              stretch && 'flex w-full',
              className,
            )}
            data-variant={variant}
            {...props}
          />
        </ScrollArea>
      </TabsContext.Provider>
    )
  },
)
TabsList.displayName = TabsPrimitive.List.displayName

interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  variant?: TabsListVariant
}

const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant, children, value, ...props }, ref) => {
  const { activeTab, uniqueId, stretch } = React.useContext(TabsContext)
  const isActive = activeTab === value

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      value={value}
      className={cn(
        'group relative inline-flex items-center justify-center whitespace-nowrap px-3 py-1 text-sm font-medium ring-offset-bg-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 gap-2 [&_svg]:size-4',
        !variant && 'rounded-md text-text-tertiary data-[state=active]:text-text-primary',
        variant === 'line' &&
          'rounded-none border-b-2 border-transparent py-2 text-text-tertiary hover:text-text-secondary data-[state=active]:text-text-primary',
        stretch && 'flex-1',
        className,
      )}
      {...props}
    >
      {isActive && !variant && (
        <motion.span
          layoutId={`bubble-default-${uniqueId}`}
          initial={false}
          className="absolute inset-0 rounded-md bg-bg-base shadow-sm mix-blend-normal"
          transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
        />
      )}

      {isActive && variant === 'line' && (
        <motion.span
          layoutId={`bubble-line-${uniqueId}`}
          initial={false}
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
          transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
        />
      )}

      <span className="relative z-10 font-medium group-data-[state=active]:font-semibold transition-all flex items-center gap-2">
        {children}
      </span>
    </TabsPrimitive.Trigger>
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    asChild
    className={cn(
      'mt-2 ring-offset-bg-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  >
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.98 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      {props.children}
    </motion.div>
  </TabsPrimitive.Content>
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsContent, TabsList, TabsTrigger }
